/**
 * Data Storage Page
 * @description 데이터 저장 위치(local, cloud) 조회 및 수정, 데이터 동기화
 */

import React, { useState, useEffect } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Radio,
  RadioGroup,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shared/ui/primitives';
import { apiClient } from '@/utils/httpClient';
import { HardDrive, Cloud, AlertCircle, CheckCircle2, RefreshCw, ArrowUpCircle, ArrowDownCircle } from 'lucide-react';

interface DataStorageConfig {
  dataStorageType: 'local' | 'cloud';
  local: { dataDir: string; logsDir: string };
  cloud: { bucket: string; region: string; credentialsConfigured: boolean };
}

interface TenantOption {
  tenantId: string;
  tenantName: string;
}

interface ServiceOption {
  folder: string;
  name: string;
  labels?: string[];
}

export const DataStoragePage: React.FC = () => {
  const [config, setConfig] = useState<DataStorageConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [services, setServices] = useState<ServiceOption[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [selectedServiceId, setSelectedServiceId] = useState('');
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncResult, setSyncResult] = useState<{
    message: string;
    filesProcessed?: number;
    errors?: number;
  } | null>(null);

  const [formData, setFormData] = useState<DataStorageConfig>({
    dataStorageType: 'local',
    local: { dataDir: './data', logsDir: './logs' },
    cloud: { bucket: 'iampam-demo', region: 'ap-northeast-2', credentialsConfigured: false },
  });

  useEffect(() => {
    loadConfig();
  }, []);

  useEffect(() => {
    if (config) {
      Promise.all([
        apiClient.get<{ tenants: TenantOption[] }>('/admin/tenants').then((d) => setTenants(d.tenants || [])),
        apiClient.get<{ services: ServiceOption[] }>('/services').then((d) => {
          const svcs = (d.services || []).filter((s) => s.labels?.includes('Service'));
          setServices(svcs);
        }),
      ]).catch(() => {});
    }
  }, [config]);

  const loadConfig = async () => {
    setIsLoading(true);
    setErrorMessage('');
    try {
      const data = await apiClient.get<DataStorageConfig>('/config/data-storage');
      setConfig(data);
      setFormData(data);
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : '설정을 불러오는데 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const runSync = async (
    endpoint: string,
    body: { direction: string; dryRun?: boolean; tenantId?: string; serviceId?: string }
  ) => {
    setSyncLoading(true);
    setSyncResult(null);
    setErrorMessage('');
    try {
      const res = await apiClient.post<{
        success: boolean;
        message: string;
        stats: { filesProcessed: number; errors: number };
      }>(endpoint, body);
      setSyncResult({
        message: res.message,
        filesProcessed: res.stats?.filesProcessed,
        errors: res.stats?.errors,
      });
    } catch (err: unknown) {
      const e = err as { response?: { message?: string }; message?: string };
      setErrorMessage(e?.response?.message || e?.message || '동기화에 실패했습니다.');
    } finally {
      setSyncLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSuccessMessage('');
    setErrorMessage('');
    try {
      await apiClient.put('/config/data-storage', {
        dataStorageType: formData.dataStorageType,
        local: formData.local,
        cloud: formData.cloud,
      });
      setSuccessMessage('저장되었습니다. 설정 반영을 위해 서버 재시작이 필요할 수 있습니다.');
      setConfig(formData);
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : '저장에 실패했습니다.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">로딩 중...</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground">데이터 저장소</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Portal 데이터 저장 위치 설정 (local / cloud). 이 설정은 Portal 및 각 서비스 Backend에 일괄 적용됩니다.
        </p>
      </div>

      {errorMessage && (
        <div className="flex items-center gap-2 p-4 rounded-lg border border-destructive/50 bg-destructive/10 text-destructive">
          <AlertCircle className="w-5 h-5 shrink-0" />
          <span>{errorMessage}</span>
        </div>
      )}

      {successMessage && (
        <div className="flex items-center gap-2 p-4 rounded-lg border border-green-500/50 bg-green-500/10 text-green-700 dark:text-green-400">
          <CheckCircle2 className="w-5 h-5 shrink-0" />
          <span>{successMessage}</span>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>저장 타입</CardTitle>
          <CardDescription>데이터를 로컬 디스크에 저장할지, Cloud(S3)에 저장할지 선택합니다.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <RadioGroup layout="inline" className="gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <Radio
                name="dataStorageType"
                value="local"
                checked={formData.dataStorageType === 'local'}
                onChange={() => setFormData((prev) => ({ ...prev, dataStorageType: 'local' }))}
              />
              <HardDrive className="w-5 h-5" />
              <span>Local (로컬 파일시스템)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <Radio
                name="dataStorageType"
                value="cloud"
                checked={formData.dataStorageType === 'cloud'}
                onChange={() => setFormData((prev) => ({ ...prev, dataStorageType: 'cloud' }))}
              />
              <Cloud className="w-5 h-5" />
              <span>Cloud (AWS S3)</span>
            </label>
          </RadioGroup>

          {formData.dataStorageType === 'local' && (
            <div className="space-y-4 pt-4 border-t">
              <div className="grid gap-2">
                <Label htmlFor="dataDir">데이터 디렉터리 (DATA_DIR)</Label>
                <Input
                  id="dataDir"
                  value={formData.local.dataDir}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      local: { ...prev.local, dataDir: e.target.value },
                    }))
                  }
                  placeholder="./data"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="logsDir">로그 디렉터리 (LOGS_DIR)</Label>
                <Input
                  id="logsDir"
                  value={formData.local.logsDir}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      local: { ...prev.local, logsDir: e.target.value },
                    }))
                  }
                  placeholder="./logs"
                />
              </div>
            </div>
          )}

          {formData.dataStorageType === 'cloud' && (
            <div className="space-y-4 pt-4 border-t">
              <div className="flex items-start gap-2 p-4 rounded-lg border border-amber-500/50 bg-amber-500/10 text-amber-800 dark:text-amber-200">
                <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                <div className="text-sm">
                  AWS 자격 증명은 <code className="px-1 py-0.5 bg-muted rounded">.env</code>의{' '}
                  <code className="px-1 py-0.5 bg-muted rounded">AWS_ACCESS_KEY_ID</code>,{' '}
                  <code className="px-1 py-0.5 bg-muted rounded">AWS_SECRET_ACCESS_KEY</code>에 설정하세요.
                  {formData.cloud.credentialsConfigured ? (
                    <span className="block mt-2 text-green-600 dark:text-green-400">✓ 자격 증명이 설정되어 있습니다.</span>
                  ) : (
                    <span className="block mt-2 text-amber-700 dark:text-amber-300">
                      ⚠ 자격 증명이 설정되지 않았습니다. .env를 확인하세요.
                    </span>
                  )}
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="bucket">S3 Bucket</Label>
                <Input
                  id="bucket"
                  value={formData.cloud.bucket}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      cloud: { ...prev.cloud, bucket: e.target.value },
                    }))
                  }
                  placeholder="iampam-demo"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="region">AWS Region</Label>
                <Input
                  id="region"
                  value={formData.cloud.region}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      cloud: { ...prev.cloud, region: e.target.value },
                    }))
                  }
                  placeholder="ap-northeast-2"
                />
              </div>
            </div>
          )}

          <div className="pt-4">
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? '저장 중...' : '저장'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="w-5 h-5" />
            데이터 동기화
          </CardTitle>
          <CardDescription>
            Local ↔ Cloud(S3) 간 데이터 동기화. AWS 자격 증명이 설정되어 있어야 합니다.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {!config?.cloud?.credentialsConfigured && (
            <div className="flex items-center gap-2 p-4 rounded-lg border border-amber-500/50 bg-amber-500/10 text-amber-800 dark:text-amber-200 text-sm">
              <AlertCircle className="w-5 h-5 shrink-0" />
              AWS 자격 증명이 설정되지 않았습니다. .env의 AWS_ACCESS_KEY_ID, AWS_SECRET_ACCESS_KEY를 설정하세요.
            </div>
          )}

          <div className="space-y-4">
            <h4 className="font-medium">전체 동기화</h4>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={syncLoading || !config?.cloud?.credentialsConfigured}
                onClick={() => runSync('/config/data-storage/migrate', { direction: 'to-cloud', dryRun: true })}
              >
                시뮬레이션 (Local→Cloud)
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={syncLoading || !config?.cloud?.credentialsConfigured}
                onClick={() =>
                  window.confirm('전체 데이터를 Local → Cloud로 동기화합니다. 계속하시겠습니까?') &&
                  runSync('/config/data-storage/migrate', { direction: 'to-cloud' })
                }
              >
                <ArrowUpCircle className="w-4 h-4 mr-1" />
                Local → Cloud
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={syncLoading || !config?.cloud?.credentialsConfigured}
                onClick={() => runSync('/config/data-storage/migrate', { direction: 'to-local', dryRun: true })}
              >
                시뮬레이션 (Cloud→Local)
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={syncLoading || !config?.cloud?.credentialsConfigured}
                onClick={() =>
                  window.confirm('전체 데이터를 Cloud → Local로 동기화합니다. 계속하시겠습니까?') &&
                  runSync('/config/data-storage/migrate', { direction: 'to-local' })
                }
              >
                <ArrowDownCircle className="w-4 h-4 mr-1" />
                Cloud → Local
              </Button>
            </div>
          </div>

          <div className="space-y-4 pt-4 border-t">
            <h4 className="font-medium">Tenant별 동기화</h4>
            <div className="flex flex-wrap items-center gap-2">
              <Select value={selectedTenantId} onValueChange={setSelectedTenantId}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Tenant 선택" />
                </SelectTrigger>
                <SelectContent>
                  {tenants.map((t) => (
                    <SelectItem key={t.tenantId} value={t.tenantId}>
                      {t.tenantName || t.tenantId}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                disabled={syncLoading || !selectedTenantId || !config?.cloud?.credentialsConfigured}
                onClick={() =>
                  runSync('/config/data-storage/sync-tenant', {
                    tenantId: selectedTenantId,
                    direction: 'to-cloud',
                    dryRun: true,
                  })
                }
              >
                시뮬레이션
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={syncLoading || !selectedTenantId || !config?.cloud?.credentialsConfigured}
                onClick={() =>
                  window.confirm(`Tenant "${selectedTenantId}" 데이터를 Local → Cloud로 동기화합니다. 계속하시겠습니까?`) &&
                  runSync('/config/data-storage/sync-tenant', {
                    tenantId: selectedTenantId,
                    direction: 'to-cloud',
                  })
                }
              >
                Local → Cloud
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={syncLoading || !selectedTenantId || !config?.cloud?.credentialsConfigured}
                onClick={() =>
                  window.confirm(`Tenant "${selectedTenantId}" 데이터를 Cloud → Local로 동기화합니다. 계속하시겠습니까?`) &&
                  runSync('/config/data-storage/sync-tenant', {
                    tenantId: selectedTenantId,
                    direction: 'to-local',
                  })
                }
              >
                Cloud → Local
              </Button>
            </div>
          </div>

          <div className="space-y-4 pt-4 border-t">
            <h4 className="font-medium">Service별 동기화</h4>
            <div className="flex flex-wrap items-center gap-2">
              <Select value={selectedServiceId} onValueChange={setSelectedServiceId}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Service 선택" />
                </SelectTrigger>
                <SelectContent>
                  {services.map((s) => (
                    <SelectItem key={s.folder} value={s.folder}>
                      {s.name || s.folder}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                disabled={syncLoading || !selectedServiceId || !config?.cloud?.credentialsConfigured}
                onClick={() =>
                  runSync('/config/data-storage/sync-service', {
                    serviceId: selectedServiceId,
                    direction: 'to-cloud',
                    dryRun: true,
                  })
                }
              >
                시뮬레이션
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={syncLoading || !selectedServiceId || !config?.cloud?.credentialsConfigured}
                onClick={() =>
                  window.confirm(
                    `Service "${selectedServiceId}" 데이터를 Local → Cloud로 동기화합니다. 계속하시겠습니까?`
                  ) &&
                  runSync('/config/data-storage/sync-service', {
                    serviceId: selectedServiceId,
                    direction: 'to-cloud',
                  })
                }
              >
                Local → Cloud
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={syncLoading || !selectedServiceId || !config?.cloud?.credentialsConfigured}
                onClick={() =>
                  window.confirm(
                    `Service "${selectedServiceId}" 데이터를 Cloud → Local로 동기화합니다. 계속하시겠습니까?`
                  ) &&
                  runSync('/config/data-storage/sync-service', {
                    serviceId: selectedServiceId,
                    direction: 'to-local',
                  })
                }
              >
                Cloud → Local
              </Button>
            </div>
          </div>

          <div className="space-y-4 pt-4 border-t">
            <h4 className="font-medium">Portal 전역만</h4>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={syncLoading || !config?.cloud?.credentialsConfigured}
                onClick={() =>
                  runSync('/config/data-storage/sync-portal', { direction: 'to-cloud', dryRun: true })
                }
              >
                시뮬레이션
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={syncLoading || !config?.cloud?.credentialsConfigured}
                onClick={() =>
                  window.confirm('Portal 데이터만 동기화합니다. 계속하시겠습니까?') &&
                  runSync('/config/data-storage/sync-portal', { direction: 'to-cloud' })
                }
              >
                Local → Cloud
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={syncLoading || !config?.cloud?.credentialsConfigured}
                onClick={() =>
                  window.confirm('Portal 데이터만 동기화합니다. 계속하시겠습니까?') &&
                  runSync('/config/data-storage/sync-portal', { direction: 'to-local' })
                }
              >
                Cloud → Local
              </Button>
            </div>
          </div>

          {syncLoading && (
            <p className="text-sm text-muted-foreground">동기화 중... 잠시 기다려 주세요.</p>
          )}
          {syncResult && !syncLoading && (
            <div className="flex items-center gap-2 p-4 rounded-lg border border-green-500/50 bg-green-500/10 text-green-700 dark:text-green-400">
              <CheckCircle2 className="w-5 h-5 shrink-0" />
              <span>
                {syncResult.message}
                {syncResult.filesProcessed != null && ` (파일: ${syncResult.filesProcessed}개`}
                {syncResult.errors != null && syncResult.errors > 0 && `, 오류: ${syncResult.errors}개`}
                {syncResult.filesProcessed != null && ')'}
              </span>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

DataStoragePage.displayName = 'DataStoragePage';
